import { styled, keyframes } from '@mui/material/styles';

import { colors } from '@/theme/variables';

const pulse = keyframes`
  0% {
    transform: scale(1) rotate(-45deg);
    opacity: 1;
  }
  50% {
    transform: scale(1.1) rotate(-45deg);
    opacity: 0.7;
  }
  100% {
    transform: scale(1) rotate(-45deg);
    opacity: 1;
  }
`;

export const UnitDragOverlayContainer = styled('div')(({ theme }) => ({
  zIndex: 9999,
  pointerEvents: 'none',
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '0',
    height: '0',
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderRight: `10px solid ${theme.palette.background.paper}`,
    zIndex: 10000,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    left: '-11px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '0',
    height: '0',
    borderTop: '11px solid transparent',
    borderBottom: '11px solid transparent',
    borderRight: `11px solid ${colors.grey[20]}`,
    zIndex: 9999,
  },

  '& .overlay-content': {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 15px',
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    border: '2px solid #007AFF',
    '& .unit-info': {
      marginLeft: '12px',
      '& div:first-of-type': {
        fontWeight: 'bold',
        fontSize: '14px',
      },
      '& div:last-of-type': {
        fontSize: '12px',
        color: '#666',
      },
    },
  },
  '& .name-box': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    marginLeft: 10,
    borderRadius: 6,
    backgroundColor: colors.grey[10],
    color: theme.palette.text.secondary,

    '& .MuiTypography-root': {
      lineHeight: '17px',
    },

    '& svg': {
      width: 17,
      height: 17,
      color: colors.grey[80],
      marginRight: 5,
    },

    '&.status-box': {
      backgroundColor: colors.grey[80],
      color: colors.white,
      fontSize: '0.875rem',
      width: 85,
      textTransform: 'capitalize',
      justifyContent: 'center',
    },
  },
}));

export const DroppableCFSContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  zIndex: 100,
  paddingBottom: '0px',

  '&.cfs-hover': {
    transform: 'scale(1.1)', // Reduced from 1.4 for smaller hover effect
    boxShadow: '0 0 15px rgba(238, 62, 92, 0.7)',

    '&:after': {
      content: '""',
      position: 'absolute',
      top: '-5px',
      left: '-5px',
      right: '-5px',
      bottom: '-5px',
      borderRadius: '50% 50% 50% 0',
      transform: 'rotate(-45deg)',
      border: `3px solid #EE3E5C`,
      animation: `${pulse} 1.5s infinite`,
      zIndex: 5,
    },
  },

  '& .cfs-tooltip': {
    position: 'absolute',
    top: '-100px', // Adjusted for smaller hover area
    left: '100%',
    transform: 'translateX(-50%)',
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    zIndex: 999,
    whiteSpace: 'nowrap',
    minWidth: '240px',
    pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '0',
      height: '0',
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderTop: '10px solid white',
    },
    '& strong': {
      fontWeight: 'bold',
      color: '#EE3E5C',
    },
    '& .drop-hint': {
      marginTop: '8px',
      fontStyle: 'italic',
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#EE3E5C',
    },
  },
}));

// Styled component for the draggable unit
export const DraggableUnitContainer = styled('div')<{
  isDragging: boolean;
  isDisabled: boolean;
}>(({ isDragging, isDisabled, theme }) => ({
  position: 'relative',
  opacity: isDragging ? 0.5 : 1,
  cursor: isDisabled ? 'default' : 'grab',
  transition: 'all 0.2s ease',
  marginBottom: '10px',
  '&:hover': {
    boxShadow: isDisabled ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  '&:active': {
    cursor: isDisabled ? 'default' : 'grabbing',
  },
  '&:focus': {
    outline: 'none',
  },
  '& .dragging-indicator': {
    position: 'absolute',
    top: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0, 0, 0, 0.8)',
    color: theme.palette.text.primary,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 1000,
  },
}));
